import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getCategories, getContentMap, getProducts } from "../api";
import { useLanguage } from "../context/LanguageContext";
import ProductCard from "../components/ProductCard";
import HeroProductCarousel from "../components/HeroProductCarousel";
import mart from "../components/mart.jpeg"

function toPath(link) {
  if (!link) return "/shop";
  if (link.startsWith("http")) return link;
  return link.startsWith("/") ? link : `/${link}`;
}

export default function Home() {
  const { lang } = useLanguage();
  const [featured, setFeatured] = useState([]);
  const [newest, setNewest] = useState([]);
  const [categories, setCategories] = useState([]);
  const [content, setContent] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [f, n, c, map] = await Promise.all([
          getProducts({ featured: 1, limit: 8 }),
          getProducts({ limit: 8 }),
          getCategories(),
          getContentMap(),
        ]);
        if (!alive) return;
        setFeatured(f);
        setNewest(n);
        setCategories(c.filter((x) => x.product_count > 0));
        setContent(map);
        if (map.seo_meta?.title) document.title = map.seo_meta.title;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc && map.seo_meta?.subtitle) {
          metaDesc.setAttribute("content", map.seo_meta.subtitle);
        }
      } catch (e) {
        if (alive) setError(e.message || "Could not load homepage. Is the PHP API + MySQL ready?");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const hero = content.hero || {};
  const heroExtra = hero.extra || {};
  const trust = content.trust_bar || {};
  const catsIntro = content.categories_intro || {};
  const promo = content.promo || {};
  const why = content.why_us || {};
  const how = content.how_it_works || {};
  const local = content.local_geo || {};
  const testimonials = content.testimonials || {};
  const faq = content.faq || {};
  const cta = content.cta_band || {};
  const featuredIntro = content.featured_intro || {};
  const newestIntro = content.newest_intro || {};

  const t = (amText, enDefault, cmsText) => {
    if (lang === "am") return amText;
    return cmsText || enDefault;
  };

  const jsonLd = useMemo(() => {
    const areas = local.extra?.areas || [];
    return {
      "@context": "https://schema.org",
      "@type": "GroceryStore",
      name: "Aki Mart",
      description:
        local.body ||
        "Habesha grocery and market in Lilburn, Georgia selling injera, dabo, snacks, and clothing.",
      url: typeof window !== "undefined" ? window.location.origin : "https://akismart.com",
      telephone: local.extra?.nap_phone || "+1 (404) 453-6512",
      address: {
        "@type": "PostalAddress",
        streetAddress: "1025 Killian Hill Rd Suite C",
        addressLocality: "Lilburn",
        addressRegion: "GA",
        postalCode: "30047",
        addressCountry: "US",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: local.extra?.geo_lat || "33.8901",
        longitude: local.extra?.geo_lng || "-84.1149",
      },
      areaServed: areas.map((a) => ({ "@type": "City", name: a })),
      image: hero.image_url,
    };
  }, [local, hero.image_url]);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <section className="hero" aria-label="Welcome">
        <div
          className="hero-bg"
          aria-hidden="true"
          style={
            hero.image_url
              ? {
                  backgroundImage: `linear-gradient(180deg, rgba(12,34,28,.4) 0%, rgba(12,34,28,.72) 45%, rgba(12,34,28,.92) 100%), url(${hero.image_url})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }
              : undefined
          }
        />
        <div className="hero-pattern" aria-hidden="true" />

        <div className="hero-stack">
          <div className="hero-upper">
            <p className="hero-brand">
              <span>{t("አኪ ማርት", "Aki Mart", heroExtra.brand_line)}</span>
            </p>
            <h1>
              {t(
                "የሀበሻ ጣዕም እና ባህላዊ አልባሳት፣ ከቤት ርቀው ለሚኖሩ::",
                "Habesha flavors & fashion for home, far from home.",
                hero.title
              )}
            </h1>
            <p className="hero-lead">
              {t(
                "ትኩስ እንጀራ፣ ዳቦ፣ ቆሎ እና በእጅ የተሰሩ የሀበሻ ቀሚሶች በሊልበርን እና አትላንታ ለሚገኙ::",
                "Fresh injera, dabo, kolos, and handmade Habesha dresses for the diaspora in Lilburn and metro Atlanta.",
                hero.subtitle
              )}
            </p>
            <div className="hero-cta">
              <Link className="btn btn-gold" to={toPath(hero.cta_link || "/shop")}>
                {t("ትክክለኛ ምርቶችን ይግዙ", "Shop genuine products", hero.cta_label)}
              </Link>
              <Link
                className="btn btn-ghost hero-ghost"
                to={toPath(heroExtra.secondary_cta_link || "/contact")}
              >
                {t("ገበያችንን ይጎብኙ", "Visit the market", heroExtra.secondary_cta_label)}
              </Link>
            </div>
          </div>

          <div className="hero-lower">
            <HeroProductCarousel products={featured} slides={heroExtra.slides} />
          </div>
        </div>
      </section>

      {error && (
        <div className="container" style={{ padding: "1.5rem 0" }}>
          <div className="flash flash-error">{error}</div>
        </div>
      )}

      {trust.is_active !== false && (trust.extra?.items?.length > 0 || trust.title) && (
        <section className="section trust-section">
          <div className="container">
            {trust.title && <h2 className="trust-heading">{t("የማህበረሰባችን አመኔታ", trust.title, trust.title)}</h2>}
            <div className="trust-grid">
            {lang === "am" && (!trust.extra?.items || trust.extra.items.length === 0 || trust.extra.items[0].title === "Authentic taste of home") ? (
              <>
                <article className="trust-card"><h3>የእውነተኛ ሀገር ቤት ጣዕም</h3><p>ከልስላሴው የጤፍ እንጀራ እስከ የተቅመመው ሀምባሻ እና የተቆላ ቆሎ — ኢትዮጵያን እና ኤርትራን የሚያስታውሱ ጣዕሞች::</p></article>
                <article className="trust-card"><h3>ለአትላንታ ነዋሪዎች ምቹ አድራሻ</h3><p>በኦንላይን ይዘዙ እና በኪሊያን ሂል ሮድ ላይ በቀላሉ ይውሰዱ — ለሊልበርን፣ ታከር፣ ክላርክስተን እና ዲኬተር ላሉ ቤተሰቦች ምቹ።</p></article>
                <article className="trust-card"><h3>ከምግብ ባሻገር</h3><p>የሀበሻ ቀሚሶችን፣ በእጅ የተሰሩ የኩሽና እቃዎችን እና ስጦታዎችን ከምግብ ሸመታዎ ጎን ያገኛሉ።</p></article>
                <article className="trust-card"><h3>ማህበረሰብ-ተኮር ገበያ</h3><p>ለሀበሻ ዳያስፖራ የተገነባ — በቅን አገልግሎት፣ በተመጣጣኝ ዋጋ እና በሚፈልጉት ምርቶች።</p></article>
              </>
            ) : (
              (trust.extra?.items || []).map((item) => (
                <article key={item.title} className="trust-card">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))
            )}
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="container">
          <div className="section-head">
            <div>
              <h2>{t("ሱቃችንን ያግኙ", "Discover our shop", catsIntro.title)}</h2>
              <p>
                {t(
                  "ልብሶች፣ ምግቦች፣ የተቆሉ መክሰሶች፣ በእጅ የተሰሩ እቃዎች — የሀገር ቤት ጣዕም::",
                  "Clothing, food, roasted snacks, handmade goods — the tastes of home.",
                  catsIntro.subtitle
                )}
              </p>
            </div>
            {catsIntro.cta_label && (
              <Link className="section-link" to={toPath(catsIntro.cta_link)}>
                {catsIntro.cta_label}
              </Link>
            )}
          </div>
          <div className="cat-strip">
            {categories.map((cat) => (
              <Link key={cat.slug} className="cat-tile" to={`/shop?category=${cat.slug}`}>
                <h3>{cat.name}</h3>
                <span>{cat.product_count} {lang === "am" ? "ምርቶች" : "items"}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div
            className="promo-band"
            style={
              promo.image_url
                ? {
                    backgroundImage: `linear-gradient(120deg, rgba(20,53,44,.92), rgba(45,106,79,.82)), url(${promo.image_url})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }
                : undefined
            }
          >
            <div>
              <h2>{t("የእኛን አቅርቦቶች ያግኙ — በዚህ ወር 30% ቅናሽ", "Discover our offerings — 30% off this month", promo.title)}</h2>
              <p>
                {t(
                  "እንጀራ፣ ዳቦ እና ተወዳጅ የጓዳ እቃዎችን ይሸምቱ። ከሊልበርን ሱቃችን ይውሰዱ::",
                  "Stock up on injera, breads, and pantry favorites. Pickup at our Lilburn suite.",
                  promo.subtitle
                )}
              </p>
              <Link className="btn btn-gold" to={toPath(promo.cta_link || "/shop")}>
                {t("አሁን ይግዙ", "Shop now", promo.cta_label)}
              </Link>
            </div>
            <div className="promo-stat">
              <strong>{promo.body || "30%"}</strong>
              <span>{t("በተመረጡ እቃዎች ላይ", "off selected items", promo.extra?.stat_label)}</span>
            </div>
          </div>
        </div>
      </section>

      

      <section className="section">
        <div className="container">
          <div className="section-head">
            <div>
              <h2>{t("ተመራጭ ምርቶች", "Featured products", featuredIntro.title)}</h2>
              <p>{t("የማህበረሰቡ ተወዳጆች — ከጤና እንጀራ እስከ ሀበሻ ቀሚሶች::", "Community favorites — from Tena Injera to Habesha dresses.", featuredIntro.subtitle)}</p>
            </div>
            <Link className="section-link" to={toPath(featuredIntro.cta_link || "/shop")}>
              {t("ሁሉንም ይመልከቱ", "View all", featuredIntro.cta_label)}
            </Link>
          </div>
          <div className="product-grid">
            {featured.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>
      <section className="section why-section">
        <div className="container why-grid">
          <div>
            <h2>{t("የሀበሻ ቤተሰቦች አኪ ማርትን ለምን ይመርጣሉ", "Why Habesha families choose Aki Mart", why.title)}</h2>
            <p className="section-lead">
              {t(
                "ለዕለት ተዕለት እና ለበዓላት አስፈላጊ ነገሮች — ሊልበርን ውስጥ::",
                "Everyday and celebration essentials for the diaspora — close to home in Lilburn.",
                why.subtitle
              )}
            </p>
            <div className="why-points">
              {lang === "am" && (!why.extra?.points || why.extra.points.length === 0 || why.extra.points[0].title === "Authentic taste of home") ? (
                <>
                  <article><h3>የእውነተኛ ሀገር ቤት ጣዕም</h3><p>ከልስላሴው የጤፍ እንጀራ እስከ የተቅመመው ሀምባሻ — የኢትዮጵያን እና የኤርትራን ጣዕም ያገኛሉ።</p></article>
                  <article><h3>ምቹ የሊልበርን አድራሻ</h3><p>በኦንላይን ይዘዙ እና በኪሊያን ሂል ሮድ ላይ በቀላሉ ይውሰዱ — ለሊልበርን፣ ታከር እና አትላንታ ላሉ ቤተሰቦች ምቹ።</p></article>
                  <article><h3>ከምግብ ባለፈ</h3><p>የሀበሻ ቀሚሶችን፣ በእጅ የተሰሩ የኩሽና እቃዎችን እና ስጦታዎችን ከምግብ ሸመታዎ ጎን ያገኛሉ።</p></article>
                  <article><h3>ማህበረሰብ-ተኮር ገበያ</h3><p>ለሀበሻ ዳያስፖራ የተገነባ — በቅን አገልግሎት፣ በተመጣጣኝ ዋጋ እና በሚፈልጉት ምርቶች።</p></article>
                </>
              ) : (
                (why.extra?.points || []).map((p) => (
                  <article key={p.title}>
                    <h3>{p.title}</h3>
                    <p>{p.text}</p>
                  </article>
                ))
              )}
            </div>
          </div>
          {why.image_url && (
            <div className="why-media">
              <img src={why.image_url} alt="" loading="lazy" />
            </div>
          )}
        </div>
      </section>

      <section className="section steps-section">
        <div className="container">
          <div className="section-head">
            <div>
              <h2>{t("በ3 ቀላል ደረጃዎች ይዘዙ", "Order in 3 easy steps", how.title)}</h2>
              <p>{t("በጆርጂያ ላሉ ስራ የበዛባቸው የሀበሻ ቤተሰቦች ቀላል የኦንላይን ግብይት::", "Simple online shopping for busy Habesha households in Georgia.", how.subtitle)}</p>
            </div>
          </div>
          <div className="steps-grid">
            {lang === "am" && (!how.extra?.steps || how.extra.steps.length === 0 || how.extra.steps[0].title === "Add to cart") ? (
              <>
                <article className="step-card"><span className="step-num">1</span><h3>ወደ ቅርጫትዎ ያስገቡ</h3><p>ምርቶችን ከኛ ትኩስ እንጀራ፣ ዳቦ እና ልዩ እቃዎች ይምረጡ።</p></article>
                <article className="step-card"><span className="step-num">2</span><h3>ደህንነቱ የተጠበቀ ክፍያ</h3><p>በኦንላይን ደህንነቱ በተጠበቀ ሁኔታ ይክፈሉ።</p></article>
                <article className="step-card"><span className="step-num">3</span><h3>በቀላሉ ይውሰዱ</h3><p>በሊልበርን ከሚገኘው ሱቃችን ያለመጉላላት ይውሰዱ።</p></article>
              </>
            ) : (
              (how.extra?.steps || []).map((step, i) => (
                <article key={step.title} className="step-card">
                  <span className="step-num">{i + 1}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="section-head">
            <div>
              <h2>{t("አዳዲስ ምርቶቻችን", "Our newest additions", newestIntro.title)}</h2>
              <p>{t("ከኩሽና እና ከዕደ-ጥበብ ማዕድ::", "Fresh from the kitchen and craft table.", newestIntro.subtitle)}</p>
            </div>
          </div>
          <div className="product-grid">
            {newest.map((product) => (
              <ProductCard key={`n-${product.id}`} product={product} />
            ))}
          </div>
        </div>
      </section>

      <section className="section local-section">
        <div className="container local-grid">
          <div>
            <h2>{t("የእርስዎ የሀበሻ ገበያ በሊልበርን፣ ጆርጂያ", "Your Habesha market in Lilburn, Georgia", local.title)}</h2>
            <p className="section-lead">{local.subtitle}</p>
            <p className="local-body">{t("ለሊልበርን፣ አትላንታ፣ ስቶን ማውንቴን፣ ስኔልቪል እና ዲኬተር ማህበረሰቦች አገልግሎት በመስጠት ላይ::", local.body || "Serving the Habesha community across Gwinnett County.", local.body)}</p>
            <div className="area-chips">
              {(local.extra?.areas || []).map((area) => (
                <span key={area} className="area-chip">
                  {area}
                </span>
              ))}
            </div>
            <p className="nap">
              {local.extra?.nap_address || "1025 Killian Hill Rd Lilburn GA, 30047 Suite C"}
              <br />
              <a href={`tel:${(local.extra?.nap_phone || "+14044536512").replace(/\D/g, "")}`}>
                {local.extra?.nap_phone || "+1 (404) 453-6512"}
              </a>
            </p>
            <Link className="btn btn-primary" to={toPath(local.cta_link || "/contact")}>
              {t("አቅጣጫዎችን ያግኙ", "Get directions", local.cta_label)}
            </Link>
          </div>
          
            <div className="local-media">
              <img src="mart" alt="Habesha market atmosphere" loading="lazy" />
            </div>
         
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <div>
              <h2>{t("ደንበኞቻችን ምን ይላሉ", "What our customers say", testimonials.title)}</h2>
              <p>{testimonials.subtitle}</p>
            </div>
          </div>
          <div className="quote-grid">
            {(testimonials.extra?.quotes || []).map((q) => (
              <blockquote key={q.name} className="quote-card">
                <p>“{q.text}”</p>
                <cite>{q.name}</cite>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="section faq-section">
        <div className="container">
          <div className="section-head">
            <div>
              <h2>{t("ከማዘዝዎ በፊት ሰዎች የሚጠይቋቸው ጥያቄዎች", "Questions people ask before ordering", faq.title)}</h2>
              <p>{faq.subtitle}</p>
            </div>
          </div>
          <div className="faq-list">
            {(faq.extra?.items || []).map((item) => (
              <details key={item.q} className="faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section diaspora-band">
        <div className="container diaspora-inner">
          <h2>{t("በአሜሪካ ላሉ የሀበሻ ማዕዶች የተዘጋጀ", "Made for Habesha tables in America", cta.title)}</h2>
          <p>
            {t(
              "ከጤፍ እንጀራ እስከ ሀበሻ ቀሚሶች — አኪ ማርት የኢትዮጵያን ገበያ ወደ ጆርጂያ ያመጣል::",
              "From teff injera to Habesha dresses — Aki Mart brings Ethiopia’s market to Georgia.",
              cta.subtitle
            )}
          </p>
          <Link className="btn btn-primary" to={toPath(cta.cta_link || "/contact")}>
            {t("አቅጣጫዎችን ያግኙ", "Get directions", cta.cta_label)}
          </Link>
        </div>
      </section>
    </>
  );
}
